import { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useNotify } from 'react-admin';
import { CriteriaBuilder, type CriteriaCondition } from '../components/CriteriaBuilder';
import { getAvailableEntityTypes } from '../schemas';
import { apiPost, ApiClientError, generateCsv, downloadCsv, calculatePageCount } from '../utils';

interface MatchingEntity {
  id: string;
  displayName: string;
  attributePreview: Record<string, unknown>;
}

interface TestResult {
  query: {
    entityType: string;
    category: string;
    criterias: string[];
    criteriaCount: number;
  };
  results: {
    count: number;
    entities: MatchingEntity[];
  };
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

type OrderDirection = 'asc' | 'desc';

export const IntersectionTester = () => {
  const notify = useNotify();

  // Entity type and category
  const [entityType, setEntityType] = useState<string>('cricket_player');
  const [category, setCategory] = useState<string>('ipl');

  // Tab for 2 or 3 criteria mode
  const [mode, setMode] = useState<number>(0); // 0 = 2 criteria, 1 = 3 criteria

  // Criteria states
  const [criteriaX, setCriteriaX] = useState<CriteriaCondition[]>([]);
  const [criteriaY, setCriteriaY] = useState<CriteriaCondition[]>([]);
  const [criteriaZ, setCriteriaZ] = useState<CriteriaCondition[]>([]);

  // DSL strings for API
  const [dslX, setDslX] = useState<string>('');
  const [dslY, setDslY] = useState<string>('');
  const [dslZ, setDslZ] = useState<string>('');

  // Results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);

  // Sorting
  const [orderBy, setOrderBy] = useState<'displayName' | 'id'>('displayName');
  const [order, setOrder] = useState<OrderDirection>('asc');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // View entity dialog
  const [viewEntity, setViewEntity] = useState<MatchingEntity | null>(null);

  const entityTypes = getAvailableEntityTypes();

  const handleTest = useCallback(async (pageOverride?: number) => {
    // Validate criteria
    const criterias: string[] = [];

    if (dslX.trim()) {
      criterias.push(dslX.trim());
    }
    if (dslY.trim()) {
      criterias.push(dslY.trim());
    }
    if (mode === 1 && dslZ.trim()) {
      criterias.push(dslZ.trim());
    }

    const minCriteria = mode === 0 ? 2 : 3;
    if (criterias.length < minCriteria) {
      notify(`Please define at least ${minCriteria} criteria`, { type: 'error' });
      return;
    }

    const currentPage = pageOverride ?? page;
    setLoading(true);
    setError(null);

    try {
      const data = await apiPost<TestResult>('/admin/api/tools/test-intersection', {
        entityType,
        category,
        criterias,
        limit,
        offset: (currentPage - 1) * limit,
      });

      setResult(data);
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'An error occurred';
      setError(message);
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [entityType, category, dslX, dslY, dslZ, mode, page, limit, notify]);

  const handleSort = (property: 'displayName' | 'id') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedEntities = result?.results.entities
    ? [...result.results.entities].sort((a, b) => {
        const aValue = a[orderBy] ?? '';
        const bValue = b[orderBy] ?? '';
        // Handle null/undefined by treating them as empty strings
        if (aValue === bValue) return 0;
        if (order === 'asc') {
          return aValue < bValue ? -1 : 1;
        }
        return aValue > bValue ? -1 : 1;
      })
    : [];

  const handleExportCsv = useCallback(() => {
    if (!result?.results.entities.length) return;

    const headers = ['ID', 'Display Name'];
    const rows = result.results.entities.map((e) => [e.id, e.displayName]);
    const csv = generateCsv(headers, rows);
    const filename = `intersection-${entityType}-${category}-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCsv(csv, filename);
  }, [result, entityType, category]);

  const handleUseInGameConfig = useCallback(() => {
    // Store criteria in session storage for game config page
    const configData = {
      entityType,
      category,
      criterias: mode === 0 ? [dslX, dslY] : [dslX, dslY, dslZ],
    };
    sessionStorage.setItem('pendingGameConfig', JSON.stringify(configData));

    // Navigate to appropriate grid creator based on mode
    if (mode === 0) {
      window.location.href = '#/grid-2d-config';
      notify('Criteria saved. Configure your 2D grid game.', { type: 'success' });
    } else {
      window.location.href = '#/grid-3d-config';
      notify('Criteria saved. Configure your 3D grid game.', { type: 'success' });
    }
  }, [entityType, category, dslX, dslY, dslZ, mode, notify]);

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Intersection Tester</Typography>
      </Box>

      {/* Entity Type and Category Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityType}
                  onChange={(e) => {
                    setEntityType(e.target.value);
                    setCriteriaX([]);
                    setCriteriaY([]);
                    setCriteriaZ([]);
                    setResult(null);
                  }}
                  label="Entity Type"
                >
                  {entityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., ipl, tamil, hindi"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Tabs
                value={mode}
                onChange={(_, newMode) => {
                  setMode(newMode);
                  setResult(null);
                }}
              >
                <Tab label="2 Criteria" />
                <Tab label="3 Criteria" />
              </Tabs>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Criteria Builders */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={mode === 0 ? 6 : 4}>
          <CriteriaBuilder
            entityType={entityType}
            value={criteriaX}
            onChange={setCriteriaX}
            onDslChange={setDslX}
            label="Criteria X (Row)"
          />
        </Grid>
        <Grid item xs={12} md={mode === 0 ? 6 : 4}>
          <CriteriaBuilder
            entityType={entityType}
            value={criteriaY}
            onChange={setCriteriaY}
            onDslChange={setDslY}
            label="Criteria Y (Column)"
          />
        </Grid>
        {mode === 1 && (
          <Grid item xs={12} md={4}>
            <CriteriaBuilder
              entityType={entityType}
              value={criteriaZ}
              onChange={setCriteriaZ}
              onDslChange={setDslZ}
              label="Criteria Z (Layer)"
            />
          </Grid>
        )}
      </Grid>

      {/* Test Button */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          onClick={() => handleTest()}
          disabled={loading}
        >
          Test Intersection
        </Button>
        {result && result.results.count > 0 && (
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<AddCircleIcon />}
              onClick={handleUseInGameConfig}
            >
              Use in Game Config
            </Button>
          </>
        )}
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Results: {result.results.count} matching entities
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Criteria: {result.query.criterias.map((c, i) => (
                  <Chip
                    key={i}
                    label={c.length > 30 ? c.substring(0, 30) + '...' : c}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                ))}
              </Typography>
            </Box>

            {result.results.count === 0 ? (
              <Alert severity="info">No entities match all criteria</Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'id'}
                            direction={orderBy === 'id' ? order : 'asc'}
                            onClick={() => handleSort('id')}
                          >
                            ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'displayName'}
                            direction={orderBy === 'displayName' ? order : 'asc'}
                            onClick={() => handleSort('displayName')}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedEntities.map((entity) => (
                        <TableRow key={entity.id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            >
                              {entity.id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {entity.displayName}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => setViewEntity(entity)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {result.pagination.hasMore && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={calculatePageCount(result.results.count, limit)}
                      page={page}
                      onChange={(_, newPage) => {
                        setPage(newPage);
                        handleTest(newPage);
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* View Entity Dialog */}
      <Dialog
        open={!!viewEntity}
        onClose={() => setViewEntity(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{viewEntity?.displayName}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Entity ID
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
            {viewEntity?.id}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Attribute Preview
          </Typography>
          <Paper
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflow: 'auto',
              maxHeight: 400,
            }}
          >
            <pre style={{ margin: 0 }}>
              {JSON.stringify(viewEntity?.attributePreview, null, 2)}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewEntity(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = `#/entities/${viewEntity?.id}/show`;
            }}
          >
            View Full Entity
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
